import { formBooleanToBoolean, mapValidationErrors } from "Utils/formUtils";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  CabinContentsProductDetailsFormValues,
  CabinContentsProductIntroFormValues,
  CabinContentsTexts,
} from "../cabinContentsInterfaces";

import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductProps } from "Components/Features/CheckPrice/Products/productInterfaces";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { RecentDamages } from "Components/Form/RecentDamages";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { nextStep } from "Store/steps/stepsSlice";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagCabinRental } from "Utils/unleash/flags";
import React from "react";
import { CABIN_CONTENTS_INTRO_STEP } from "..";
import { ElectricityQuestion } from "../../FormComponentsHouseProducts/FormComponentsCabinProducts/ElectricityQuestion";
import { InsuranceSumInput } from "../../FormComponentsHouseProducts/FormComponentsCabinProducts/InsuranceSumInput";
import { RentalQuestion } from "../../FormComponentsHouseProducts/FormComponentsCabinProducts/RentalQuestion";
import { TapWaterQuestion } from "../../FormComponentsHouseProducts/FormComponentsCabinProducts/TapWaterQuestion";
import { CABIN_CONTENTS_ABOUT_STEP } from "../CabinContentsSteps";
import { validationSchema } from "./cabinContentsProductDetailsValidation";

export const CabinContentsProductDetails = ({
  updatePriceRequest,
}: ProductProps): React.ReactElement => {
  const [{ details }, lang] = useProductTexts<CabinContentsTexts>(
    getFMSProductName(INSURANCE_NAME_VALUE.CABIN_CONTENTS),
  );
  const dispatch = useDispatch();
  const initialFormState = useFormState<CabinContentsProductDetailsFormValues>(
    CABIN_CONTENTS_ABOUT_STEP.id,
    initialFormValues,
  );
  const {
    priceCalculation: { priceCalculationFormStates, priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const cabinContentsProductIntroFormValues = priceCalculationFormStates[
    CABIN_CONTENTS_INTRO_STEP.id
  ] as CabinContentsProductIntroFormValues;

  const enableRentalToggle = useUnleashFeatureFlag(
    FeatureFlagCabinRental.ENABLE_CABIN_RENTAL,
  );

  const handleSubmit = (
    formValues: CabinContentsProductDetailsFormValues,
  ): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: CABIN_CONTENTS_ABOUT_STEP.id,
        formState: {
          ...formValues,
        },
      }),
    );
    dispatch(
      updatePriceRequest({
        productPriceUrl: BackendProductInfo.CABIN_CONTENTS.priceReqUrl,
        stepForwardOnSuccess: true,
        updatedProductPriceRequestInfo: {
          postNumber: cabinContentsProductIntroFormValues.postNumber,
          hasRental: enableRentalToggle
            ? formBooleanToBoolean(formValues.hasRental)
            : undefined,
          hasElectricity: formBooleanToBoolean(formValues.hasElectricity),
          hasTapWater: formBooleanToBoolean(formValues.hasTapWater),
          injuries: Number(formValues.injuries),
          injuryYear: Number(formValues.injuryYear),
          contentAmount: formValues.contentsInsuranceSum,
        },
      }),
    );

    dispatch(nextStep());
  };

  return (
    <ProductDetailsTile
      header={CABIN_CONTENTS_ABOUT_STEP.title}
      validationSchema={validationSchema()}
      initialValues={initialFormState}
      onSubmit={handleSubmit}
    >
      {(formProps: FormikProps<CabinContentsProductDetailsFormValues>) => {
        return (
          <Form>
            <ElectricityQuestion formProps={formProps} lang={lang} />
            <TapWaterQuestion formProps={formProps} lang={lang} />
            <RentalQuestion formProps={formProps} lang={lang} />
            <InsuranceSumInput formProps={formProps} lang={lang} />
            <RecentDamages
              formProps={formProps}
              productName={ProductBackendName.CABIN_CONTENTS}
            />
            <BackendErrorAlert priceCalculation={priceCalculationResult} />
            <ErrorsSummedUp
              errors={mapValidationErrors(formProps.errors, details, lang)}
              isVisible
            />
            <ScrollToFirstError />
            <NavigationButtons isLoading={priceCalculationResult.isLoading} />
          </Form>
        );
      }}
    </ProductDetailsTile>
  );
};

const initialFormValues = {
  hasElectricity: undefined,
  hasTapWater: undefined,
  hasRental: undefined,
  rentalTimeExceedsOneMonthAYear: undefined,
  contentsInsuranceSum: undefined,
  injuries: undefined,
  injuryYear: undefined,
};
